// Centralized theme configuration for Relo Portal
// All components should import colors from here instead of defining locally

export const colors = {
  // Backgrounds
  bgPrimary: '#171717',
  bgSecondary: '#1f1f1f',
  bgTertiary: '#262626',

  // Borders
  borderDefault: '#2e2e2e',
  borderSubtle: '#262626',

  // Text
  textPrimary: '#ededed',
  textSecondary: '#a1a1a1',
  textMuted: '#666666',

  // Brand
  brand: '#3ecf8e',
  brandMuted: 'rgba(62, 207, 142, 0.15)',
  brandHover: '#4ade80',

  // Categories (Product Lines) — aligned with tailwind.config.js category colors
  categoryMx: '#3b82f6',    // Blue - Galaxy/MX
  categoryVd: '#22c55e',    // Green - Visual Display
  categoryDa: '#f97316',    // Orange - Digital Appliances

  // Status colors
  success: '#22c55e',
  successBg: 'rgba(34, 197, 94, 0.15)',
  warning: '#eab308',
  warningBg: 'rgba(234, 179, 8, 0.15)',
  error: '#ef4444',
  errorBg: 'rgba(239, 68, 68, 0.15)',
  info: '#f97316',          // Orange instead of blue
  purple: '#ec4899',        // Pink instead of purple
  pink: '#ec4899',

  // Additional UI colors
  blue: '#3b82f6',
  indigo: '#6366f1',
  emerald: '#10b981',
  amber: '#f59e0b',
  cyan: '#06b6d4',
  violet: '#a855f7',
  gray: '#6b7280',
  lime: '#84cc16',
  teal: '#14b8a6',

  // Semantic
  positive: '#22c55e',
  negative: '#ef4444',
  neutral: '#a1a1a1',
} as const;

// Status badge configurations
export const statusColors: Record<string, { bg: string; text: string }> = {
  // Partner statuses
  Activo: { bg: 'rgba(62, 207, 142, 0.15)', text: colors.brand },
  Pausado: { bg: 'rgba(234, 179, 8, 0.15)', text: colors.warning },
  Prospecto: { bg: 'rgba(249, 115, 22, 0.15)', text: colors.info },
  Contactado: { bg: 'rgba(236, 72, 153, 0.15)', text: colors.purple },
  Negociando: { bg: 'rgba(249, 115, 22, 0.15)', text: colors.categoryDa },
  Churned: { bg: 'rgba(239, 68, 68, 0.15)', text: colors.error },

  // Document statuses
  pending: { bg: 'rgba(234, 179, 8, 0.15)', text: colors.warning },
  approved: { bg: 'rgba(62, 207, 142, 0.15)', text: colors.brand },
  rejected: { bg: 'rgba(239, 68, 68, 0.15)', text: colors.error },
  expired: { bg: 'rgba(239, 68, 68, 0.15)', text: colors.error },

  // Payment statuses
  processing: { bg: 'rgba(249, 115, 22, 0.15)', text: colors.info },
  paid: { bg: 'rgba(62, 207, 142, 0.15)', text: colors.brand },
  cancelled: { bg: 'rgba(102, 102, 102, 0.15)', text: colors.textMuted },

  // Asset statuses
  active: { bg: 'rgba(62, 207, 142, 0.15)', text: colors.brand },
  archived: { bg: 'rgba(102, 102, 102, 0.15)', text: colors.textMuted },
  pending_review: { bg: 'rgba(234, 179, 8, 0.15)', text: colors.warning },

  // Import statuses
  done: { bg: 'rgba(16, 185, 129, 0.2)', text: colors.success },
  completed: { bg: 'rgba(16, 185, 129, 0.2)', text: colors.success },
  failed: { bg: 'rgba(239, 68, 68, 0.2)', text: colors.error },
  error: { bg: 'rgba(239, 68, 68, 0.2)', text: colors.error },
};

// Segment colors — single source of truth for frontend. Backend copy at src/workers/handlers/utils.ts
export const SEGMENT_COLORS: Record<string, string> = {
  MX: '#3b82f6',   // Blue - Galaxy/Mobile
  VD: '#22c55e',   // Green - Visual Display
  DA: '#f97316',   // Orange - Digital Appliances
  IT: '#ef4444',   // Red - IT/Computing
  NW: '#6366f1',   // Indigo - Network
  TAB: '#14b8a6',  // Teal - Tablets
  WR: '#eab308',   // Yellow - Wearables (Buds & Watches)
  OTHER: '#8b5cf6', // Purple - Other
};

// Category icons mapping (code -> color)
export const categoryColors: Record<string, string> = {
  banner: colors.info,
  logo: colors.purple,
  video: colors.error,
  html5: colors.warning,
  copy: colors.brand,
  feed: colors.categoryDa,
  landing: colors.pink,
  other: colors.textMuted,
};

// Spacing scale
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
} as const;

// Border radius
export const radius = {
  sm: '4px',
  md: '8px',
  lg: '10px',
  xl: '12px',
  xxl: '16px',
  full: '9999px',
} as const;

// Shadows
export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.3)',
} as const;

// Typography
export const typography = {
  fontFamily: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
  },
  fontSize: {
    xs: '10px',
    sm: '12px',
    md: '13px',
    base: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '20px',
    xxxl: '24px',
    display: '28px',
    hero: '42px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

// Month names in Spanish
export const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
] as const;

// Helper to format currency
export const formatCurrency = (amount: number, currency = 'MXN'): string => {
  return `$${amount.toLocaleString()} ${currency}`;
};

// Helper to format file size
export const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Helper to get status color
export const getStatusColor = (status: string): { bg: string; text: string } => {
  return statusColors[status] || { bg: colors.bgTertiary, text: colors.textSecondary };
};

// =============================================================================
// RESPONSIVE DESIGN TOKEN SYSTEM
// =============================================================================

/**
 * Breakpoints for responsive design
 * Usage: @media (max-width: ${breakpoints.tablet}px) { ... }
 */
export const breakpoints = {
  /** Small mobile: 0-479px */
  smallMobile: 480,
  /** Mobile: 0-767px */
  mobile: 768,
  /** Tablet: 768-1023px */
  tablet: 1024,
  /** Desktop: 1024-1279px */
  desktop: 1280,
  /** Large desktop: 1280px+ */
  largeDesktop: 1440,
} as const;

/**
 * Responsive spacing values
 * Returns different values based on screen size
 */
export const responsiveSpacing = {
  /** Page padding: 16px mobile, 24px tablet, 32px desktop */
  pagePadding: { mobile: '16px', tablet: '24px', desktop: '32px' },
  /** Section gap: 16px mobile, 24px desktop */
  sectionGap: { mobile: '16px', desktop: '24px' },
  /** Card padding: 12px mobile, 16px tablet, 20px desktop */
  cardPadding: { mobile: '12px', tablet: '16px', desktop: '20px' },
  /** Grid gap: 8px mobile, 12px tablet, 16px desktop */
  gridGap: { mobile: '8px', tablet: '12px', desktop: '16px' },
  /** Form gap: 12px mobile, 16px desktop */
  formGap: { mobile: '12px', desktop: '16px' },
} as const;

/**
 * Responsive typography sizes
 */
export const responsiveTypography = {
  /** Page title: 20px mobile, 24px tablet, 28px desktop */
  pageTitle: { mobile: '20px', tablet: '24px', desktop: '28px' },
  /** Section title: 16px mobile, 18px desktop */
  sectionTitle: { mobile: '16px', desktop: '18px' },
  /** Card title: 14px mobile, 16px desktop */
  cardTitle: { mobile: '14px', desktop: '16px' },
  /** Body text: 13px mobile, 14px desktop */
  body: { mobile: '13px', desktop: '14px' },
  /** Small text: 11px mobile, 12px desktop */
  small: { mobile: '11px', desktop: '12px' },
} as const;

/**
 * Grid configurations for responsive layouts
 */
export const responsiveGrid = {
  /** Stats grid: 1 col mobile, 2 col tablet, 4 col desktop */
  stats: { mobile: 1, tablet: 2, desktop: 4 },
  /** Cards grid: 1 col mobile, 2 col tablet, 3 col desktop */
  cards: { mobile: 1, tablet: 2, desktop: 3 },
  /** Products grid: 2 col mobile, 3 col tablet, 5 col desktop */
  products: { mobile: 2, tablet: 3, desktop: 5 },
  /** Files grid: 2 col mobile, 3 col tablet, auto-fill desktop */
  files: { mobile: 2, tablet: 3, desktop: 'auto-fill' },
  /** Form columns: 1 col mobile, 2 col tablet, 3 col desktop */
  form: { mobile: 1, tablet: 2, desktop: 3 },
} as const;

/**
 * Helper to get responsive value based on isMobile flag
 */
export function getResponsiveValue<T>(
  values: { mobile: T; tablet?: T; desktop: T },
  isMobile: boolean,
  isTablet?: boolean
): T {
  if (isMobile) return values.mobile;
  if (isTablet && values.tablet) return values.tablet;
  return values.desktop;
}

/**
 * Helper to build grid template columns
 */
export function getGridColumns(
  config: { mobile: number | string; tablet?: number | string; desktop: number | string },
  isMobile: boolean,
  isTablet?: boolean
): string {
  const cols = getResponsiveValue(config, isMobile, isTablet);
  if (typeof cols === 'number') {
    return `repeat(${cols}, 1fr)`;
  }
  if (cols === 'auto-fill') {
    return 'repeat(auto-fill, minmax(150px, 1fr))';
  }
  return cols;
}

/**
 * Common responsive style patterns
 * Use with spread operator: style={{ ...responsiveStyles.card(isMobile) }}
 */
export const responsiveStyles = {
  /** Page container with responsive padding */
  pageContainer: (isMobile: boolean) => ({
    padding: isMobile ? '16px' : '24px',
    maxWidth: isMobile ? '100%' : undefined,
  }),

  /** Card with responsive padding */
  card: (isMobile: boolean) => ({
    padding: isMobile ? '12px' : '16px',
    borderRadius: isMobile ? '8px' : '10px',
  }),

  /** Flex container that stacks on mobile */
  flexStack: (isMobile: boolean) => ({
    display: 'flex',
    flexDirection: (isMobile ? 'column' : 'row') as 'column' | 'row',
    gap: isMobile ? '12px' : '16px',
  }),

  /** Stats grid with responsive columns */
  statsGrid: (isMobile: boolean) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
    gap: isMobile ? '8px' : '16px',
  }),

  /** Cards grid with responsive columns */
  cardsGrid: (isMobile: boolean, isTablet?: boolean) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
    gap: isMobile ? '12px' : '16px',
  }),

  /** Products grid with responsive columns */
  productsGrid: (isMobile: boolean) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
    gap: isMobile ? '8px' : '12px',
  }),

  /** Form layout with responsive columns */
  formGrid: (isMobile: boolean) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: isMobile ? '12px' : '16px',
  }),

  /** Page title with responsive size */
  pageTitle: (isMobile: boolean) => ({
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: 600,
  }),

  /** Section title with responsive size */
  sectionTitle: (isMobile: boolean) => ({
    fontSize: isMobile ? '16px' : '18px',
    fontWeight: 600,
  }),

  /** Modal with responsive width */
  modal: (isMobile: boolean) => ({
    width: isMobile ? '95vw' : '600px',
    maxWidth: isMobile ? '100%' : '90vw',
    maxHeight: isMobile ? '90vh' : '85vh',
    padding: isMobile ? '16px' : '24px',
  }),

  /** Table container with horizontal scroll on mobile */
  tableContainer: (isMobile: boolean) => ({
    overflowX: isMobile ? ('auto' as const) : ('visible' as const),
    WebkitOverflowScrolling: 'touch' as const,
  }),

  /** Hide on mobile */
  hideOnMobile: (isMobile: boolean) => ({
    display: isMobile ? 'none' : undefined,
  }),

  /** Show only on mobile */
  showOnMobile: (isMobile: boolean) => ({
    display: isMobile ? undefined : 'none',
  }),
} as const;

/**
 * CSS media query strings for use in styled components or CSS-in-JS
 */
export const mediaQueries = {
  smallMobile: `@media (max-width: ${breakpoints.smallMobile - 1}px)`,
  mobile: `@media (max-width: ${breakpoints.mobile - 1}px)`,
  tablet: `@media (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.tablet - 1}px)`,
  desktop: `@media (min-width: ${breakpoints.tablet}px)`,
  largeDesktop: `@media (min-width: ${breakpoints.largeDesktop}px)`,
} as const;
